import { toast } from "react-toastify";

// Documentos da empresa (/c/documents) e do colaborador (/e/documents): tipo e
// tamanho do arquivo e o download.

/** Nome do arquivo gravado no disco/S3 (o fim do `document_uri`, com o hash). */
const storedFilename = (doc) => (doc?.document_uri ? String(doc.document_uri).split("/").pop() : "");

/** Extensão do arquivo em maiúsculas ("PDF"), pelo `document_uri`; "" sem extensão. */
export function documentExtension(doc) {
  const name = storedFilename(doc);
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : "";
}

/** Tamanho em bytes por extenso: "13 B", "739 KB", "1,5 MB", "12 MB"; "" sem tamanho. */
export function formatFileSize(bytes) {
  const size = Number(bytes);
  if (bytes === null || bytes === undefined || bytes === "" || !Number.isFinite(size) || size < 0) return "";
  if (size < 1024) return `${size} B`;
  const units = ["KB", "MB", "GB"];
  let value = size / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // KB sem decimal; MB e GB com uma casa abaixo de 10
  const digits = unit > 0 && value < 10 ? 1 : 0;
  const text = value.toLocaleString("pt-BR", { maximumFractionDigits: digits, useGrouping: false });
  return `${text} ${units[unit]}`;
}

/** Linha de apoio do documento: "PDF · 739 KB". */
export function documentMeta(doc) {
  return [documentExtension(doc), formatFileSize(doc?.size)].filter(Boolean).join(" · ");
}

/** Nome do arquivo baixado: o do documento com a extensão ("Contrato.pdf"). */
export function documentDownloadName(doc) {
  const extension = documentExtension(doc).toLowerCase();
  const name = (doc?.filename || "").trim();
  if (!name) return storedFilename(doc) || "documento";
  if (!extension || name.toLowerCase().endsWith(`.${extension}`)) return name;
  return `${name}.${extension}`;
}

/**
 * Baixa o arquivo de um documento.
 *
 * @param {object} documentsService - `useService("documents")`
 * @param {object} doc - documento como vem da API (`filename`, `document_uri`)
 */
export async function downloadDocument(documentsService, doc) {
  try {
    const result = await documentsService.getDocument(storedFilename(doc));
    // Em erro, o service já mostrou o toast
    if (!result || result.error) return;

    const link = document.createElement("a");
    if (result.url) {
      // S3: abre a URL assinada diretamente (sem enviar header Authorization)
      link.href = result.url;
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    } else if (result instanceof Blob) {
      // Armazenamento local: usa blob
      link.href = URL.createObjectURL(result);
    } else {
      throw new Error("Resposta inesperada do download");
    }
    link.setAttribute("download", documentDownloadName(doc));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (!result.url) URL.revokeObjectURL(link.href);
  } catch (err) {
    console.error(err);
    toast.error("Erro ao baixar o documento");
  }
}
